import { Routes, Route } from 'react-router-dom'
import { LanguageProvider } from './contexts/LanguageContext'
import { ThemeProvider } from './contexts/ThemeContext'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import BetaPage from './pages/BetaPage'
import DashboardPage from './pages/DashboardPage'
import GroupsPage from './pages/GroupsPage'
import BetaPageDebug from './pages/BetaPageDebug'
import MaintenancePage from './pages/MaintenancePage'
import AdminPanel from './components/admin/AdminPanel'
import AdminCheck from './components/auth/AdminCheck'
import AdminAccessDenied from './components/admin/AdminAccessDenied'

const truthyValues = ['true', '1', 'yes', 'on']

const parseTruthy = (value: unknown): boolean => {
  return truthyValues.includes(String(value ?? '').trim().toLowerCase())
}

type MaintenanceState = {
  enabled: boolean
  source: 'env' | 'query' | 'localStorage' | 'window'
}

const getRuntimeMaintenanceState = (): MaintenanceState | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const queryValue = new URLSearchParams(window.location.search).get('maintenance')
  if (queryValue !== null) {
    return parseTruthy(queryValue)
      ? { enabled: true, source: 'query' }
      : null
  }

  const storageValue = window.localStorage.getItem('maintenance_mode')
  if (storageValue !== null) {
    return parseTruthy(storageValue)
      ? { enabled: true, source: 'localStorage' }
      : null
  }

  const globalValue = (window as Window & { __MAINTENANCE_MODE__?: unknown }).__MAINTENANCE_MODE__
  if (globalValue !== undefined) {
    return parseTruthy(globalValue)
      ? { enabled: true, source: 'window' }
      : null
  }

  return null
}

function App() {
  // Accept legacy/misspelled env names and common truthy values.
  const maintenanceFlag =
    import.meta.env.VITE_MAINTENANCE_MODE ??
    import.meta.env.VITE_MAINTANANCE_MODE ??
    import.meta.env.VITE_MAINTAINANCE_MODE

  const runtimeMaintenanceState = getRuntimeMaintenanceState()
  const envMaintenanceEnabled = parseTruthy(maintenanceFlag)
  const maintenanceState: MaintenanceState | null =
    runtimeMaintenanceState ?? (envMaintenanceEnabled ? { enabled: true, source: 'env' } : null)

  if (maintenanceState?.enabled) {
    return (
      <ThemeProvider>
        <LanguageProvider>
          <MaintenancePage source={maintenanceState.source} />
        </LanguageProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/beta" element={<BetaPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/beta-debug" element={<BetaPageDebug />} />
          <Route path="/admin" element={
            <AdminCheck fallback={<AdminAccessDenied />}>
              <AdminPanel />
            </AdminCheck>
          } />
        </Routes>
      </LanguageProvider>
    </ThemeProvider>
  )
}

export default App